/**
 * CPP/OAS Benefit Calculator Integration
 *
 * Provides methods to calculate and suggest Canadian pension benefits
 * based on user profile (age, retirement status, residency, income).
 *
 * Used by Income form to provide intelligent default values or
 * suggestions that users can accept or override.
 */

import { calculateCPPBenefit } from "../services/cppCalculationService";
import { calculateOASBenefit } from "../services/oasCalculationService";
import { createLogger } from "../utils/logger";

const pensionLogger = createLogger({ prefix: "PensionCalculator" });

/**
 * Parameters needed to calculate Canadian pension benefits
 */
export interface PensionCalculationParams {
  userAge: number | null;
  isRetired: boolean | null;
  yearsInCanada: number | null;
  annualIncome: number;
}

/**
 * Result of pension calculation
 */
export interface PensionCalculationResult {
  cppSuggested: number | null; // Suggested CPP benefit or null if not eligible
  oasSuggested: number | null; // Suggested OAS benefit or null if not eligible
  reason: string; // Explanation of why benefits are/aren't eligible
}

/**
 * Calculate suggested Canadian pension benefits
 *
 * @param params User profile parameters
 * @returns Calculated CPP/OAS benefits with eligibility explanation
 *
 * @example
 * const result = calculateSuggestedPensions({
 *   userAge: 65,
 *   isRetired: true,
 *   yearsInCanada: 40,
 *   annualIncome: 75000,
 * });
 * // Result: { cppSuggested: 1433.85, oasSuggested: 713.34, reason: "... }
 */
export function calculateSuggestedPensions(
  params: PensionCalculationParams
): PensionCalculationResult {
  const { userAge, isRetired, yearsInCanada, annualIncome } = params;

  // If we don't have age or retirement info, can't calculate
  if (userAge === null || isRetired === null) {
    return {
      cppSuggested: null,
      oasSuggested: null,
      reason:
        "Complete your profile (age and retirement status) to calculate pension benefits",
    };
  }

  let cppSuggested: number | null = null;
  let oasSuggested: number | null = null;
  let reason = "";

  // CPP: Only if retired and age 60+
  if (isRetired && userAge >= 60) {
    try {
      // Assume full contribution years if not specified
      // (users can override/adjust)
      cppSuggested = calculateCPPBenefit({
        age: userAge,
        contributionYears: 39, // Assume maximum for initial suggestion
      });
      reason += `CPP calculated for age ${userAge}. `;
    } catch (error) {
      pensionLogger.error("CPP benefit calculation failed", { error, userAge });
      throw error;
    }
  } else if (isRetired && userAge < 60) {
    reason += `CPP not available before age 60. `;
  } else if (!isRetired && userAge >= 60) {
    reason += `CPP available at full retirement age (65) or earlier with reduction. `;
  } else if (!isRetired) {
    reason += `CPP available at retirement or age 60 (whichever is later). `;
  }

  // OAS: Only if age 65+
  if (userAge >= 65) {
    try {
      // Use actual years in Canada if provided, otherwise assume eligible
      const residencyYears =
        yearsInCanada !== null && yearsInCanada > 0 ? yearsInCanada : 40;

      oasSuggested = calculateOASBenefit({
        age: userAge,
        yearsInCanada: residencyYears,
        annualIncome: annualIncome,
      });

      if (oasSuggested > 0) {
        reason += `OAS calculated for age ${userAge}`;
        if (yearsInCanada !== null && yearsInCanada < 40) {
          reason += ` with ${yearsInCanada} years residency (pro-rated). `;
        } else {
          reason += ` (maximum with 40+ years residency). `;
        }

        // Add clawback note if applicable
        if (annualIncome > 90997) {
          reason += `Income clawback applied. `;
        }
      } else {
        reason += `OAS fully clawed back due to income. `;
      }
    } catch (error) {
      pensionLogger.error("OAS benefit calculation failed", { error, userAge });
      throw error;
    }
  } else {
    reason += `OAS available at age 65. `;
  }

  return {
    cppSuggested,
    oasSuggested,
    reason: reason.trim(),
  };
}

/**
 * Check if user might be eligible for CPP
 * (quick eligibility check without full calculation)
 */
export function canQualifyForCPP(
  userAge: number | null,
  isRetired: boolean | null
): boolean {
  return isRetired === true && userAge !== null && userAge >= 60;
}

/**
 * Check if user might be eligible for OAS
 * (quick eligibility check without full calculation)
 */
export function canQualifyForOAS(userAge: number | null): boolean {
  return userAge !== null && userAge >= 65;
}
